import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/client';

const TYPE_SHORT = { software:'SW', license:'LIC', hardware:'HW', appliance:'AP' };
const TYPE_BADGE = { software:'badge-blue', license:'badge-purple', hardware:'badge-gray', appliance:'badge-green' };

// 자주 쓰는 항목 통합 모달 — 저장된 묶음 / 즐겨찾기 / 전체 3개 탭.
// 드래그 시작 → 모달 투명 + pointer-events:none → 그 아래 견적 항목 카드에 드롭 가능.
// 닫기: backdrop 클릭 / ✕ / ESC.
//
// props:
//   open: boolean
//   onClose: () => void
//   products / parts: 부모에서 전달 (FavoriteTray 가 받던 그대로)
//   favorites: { products:[id], parts:[id] }
//   onToggleFavorite(kind, id)
//   onAddProduct(p) / onAddPart(p) — 클릭 추가
//   onInsertBundleById(bundleId) — 묶음 클릭 시 호출
export default function ItemPickerModal({
  open, onClose,
  products = [], parts = [],
  favorites,
  onToggleFavorite,
  onAddProduct, onAddPart,
  onInsertBundleById,
  hideBundles = false,
}) {
  const defaultTab = hideBundles ? 'fav' : 'bundles';
  const [tab, setTab] = useState(defaultTab);
  const [bundles, setBundles] = useState([]);
  const [qBundle, setQBundle] = useState('');
  const [qFav, setQFav] = useState('');
  const [qAll, setQAll] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  // 열릴 때 묶음 목록 로드
  useEffect(() => {
    if (!open) return;
    setTab(defaultTab);
    if (!hideBundles) api.get('/bundles').then(setBundles).catch(() => setBundles([]));
  }, [open, defaultTab, hideBundles]);

  // ESC 닫기 (드래그 중에는 무시)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isDragging) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isDragging, onClose]);

  // 안전망 — 카드의 onDragEnd 가 호출 안 되는 경우(브라우저 차이) 를 대비해
  // window 레벨에서 dragend/drop 을 잡아 isDragging 을 항상 해제 + body 클래스로 드롭존 강조
  useEffect(() => {
    if (!isDragging) {
      document.body.classList.remove('has-picker-drag');
      return;
    }
    document.body.classList.add('has-picker-drag');
    const reset = () => setIsDragging(false);
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    const onKey = (e) => { if (e.key === 'Escape') reset(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('has-picker-drag');
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
      window.removeEventListener('keydown', onKey);
    };
  }, [isDragging]);

  // 드래그 헬퍼
  // ★ Chrome 회피책 — dragstart 핸들러 안에서 부모의 transform 을 동기 적용하면 drag 가 즉시 cancel 되므로
  //   setIsDragging 을 requestAnimationFrame 으로 한 frame 미룬다.
  // ★ 짧은 drag 후 click 이 함께 발화되어 onClick(추가+닫기) 가 트리거되는 케이스를 ref 가드로 차단한다.
  const dragJustStartedRef = useRef(false);
  const startDrag = (e, payload) => {
    e.dataTransfer.setData('application/x-soosan-item', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
    dragJustStartedRef.current = true;
    requestAnimationFrame(() => setIsDragging(true));
  };
  const endDrag = () => {
    setIsDragging(false);
    setTimeout(() => { dragJustStartedRef.current = false; }, 150);
  };

  // 즐겨찾기 카드 (제품/부품 공용)
  const renderFavCard = (kind, p) => {
    const isFav = (favorites?.[kind === 'product' ? 'products' : 'parts'] || []).includes(p.id);
    const onClick = () => {
      // drag 직후 발화되는 click 은 무시 (drag → click 으로 인한 모달 닫힘/추가 방지)
      if (dragJustStartedRef.current) return;
      if (kind === 'product') onAddProduct(p); else onAddPart(p);
      onClose();
    };
    return (
      <div key={`${kind}-${p.id}`} className="picker-card"
        draggable={true}
        onDragStart={(e)=>startDrag(e, { kind, item: p })}
        onDragEnd={endDrag}
        onClick={onClick}
        title="드래그 또는 클릭으로 추가">
        <span className="picker-card-icon">{kind === 'product' ? '📦' : '🔩'}</span>
        <div className="picker-card-body">
          <div className="picker-card-name">
            {kind === 'product' && p.product_type && (
              <span className={`badge ${TYPE_BADGE[p.product_type]||'badge-gray'}`}
                style={{ marginRight:6, fontSize:10, padding:'1px 5px' }}>
                {TYPE_SHORT[p.product_type]||p.product_type}
              </span>
            )}
            {p.name}
          </div>
          <div className="picker-card-sub">
            {kind === 'product' ? (p.model || '') : (p.spec || '')}
            {kind === 'part' && p.purchase_price != null && (
              <span style={{ marginLeft:6, color:'#94a3b8' }}>₩{Number(p.purchase_price).toLocaleString()}</span>
            )}
          </div>
        </div>
        {/* button → div role=button : <button> 은 native drag 를 차단하므로 div 로 대체 */}
        <div className="picker-star" role="button" tabIndex={0}
          aria-label={isFav?'즐겨찾기 해제':'즐겨찾기 추가'}
          onClick={(e)=>{e.stopPropagation(); onToggleFavorite(kind === 'product' ? 'products' : 'parts', p.id);}}
          onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') { e.preventDefault(); e.stopPropagation(); onToggleFavorite(kind === 'product' ? 'products' : 'parts', p.id); }}}
          onMouseDown={(e)=>e.stopPropagation()}
          draggable={false}>
          {isFav ? '★' : '☆'}
        </div>
      </div>
    );
  };

  // ===== 탭별 데이터 =====
  const favProducts = useMemo(
    () => (favorites?.products || []).map(id => products.find(p => p.id === id)).filter(Boolean),
    [favorites, products]
  );
  const favParts = useMemo(
    () => (favorites?.parts || []).map(id => parts.find(p => p.id === id)).filter(Boolean),
    [favorites, parts]
  );

  const matchAny = (item, keys, q) => !q || keys.some(k => (item[k] || '').toString().toLowerCase().includes(q.toLowerCase()));

  const filteredFavProducts = useMemo(
    () => favProducts.filter(p => matchAny(p, ['name','model','alias','code'], qFav)),
    [favProducts, qFav]
  );
  const filteredFavParts = useMemo(
    () => favParts.filter(p => matchAny(p, ['name','spec','code'], qFav)),
    [favParts, qFav]
  );

  const allProducts = useMemo(
    () => products.filter(p => matchAny(p, ['name','model','alias','code'], qAll)).slice(0, 200),
    [products, qAll]
  );
  const allParts = useMemo(
    () => parts.filter(p => matchAny(p, ['name','spec','code'], qAll)).slice(0, 200),
    [parts, qAll]
  );

  const filteredBundles = useMemo(
    () => bundles.filter(b => !qBundle || b.name.toLowerCase().includes(qBundle.toLowerCase()) || (b.description||'').toLowerCase().includes(qBundle.toLowerCase())),
    [bundles, qBundle]
  );

  const insertBundle = async (bundle) => {
    if (busy) return;
    if (dragJustStartedRef.current) return;
    if (!onInsertBundleById) return;
    setBusy(true);
    try {
      await onInsertBundleById(bundle.id);
      onClose();
    } catch (e) { /* 호출자가 토스트 처리 */ }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className={`modal-backdrop picker-modal-backdrop${isDragging ? ' is-dragging' : ''}`}
      role="dialog" aria-modal="true" aria-label="자주 쓰는 항목"
      onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:90, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div className="picker-modal-content" onClick={(e)=>e.stopPropagation()}
        style={{ width:900, maxWidth:'90vw', maxHeight:'80vh', background:'#fff', borderRadius:12, border:'1px solid #e2e8f0', padding:24, display:'flex', flexDirection:'column' }}>

        {/* 헤더 */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h2 style={{ fontSize:17, fontWeight:700, color:'#1e293b' }}>⭐ 자주 쓰는 항목</h2>
          <span style={{ cursor:'pointer', color:'#94a3b8', fontSize:16 }} onClick={onClose} aria-label="닫기">✕</span>
        </div>

        {/* 탭 바 */}
        <div className="picker-modal-tabs">
          {!hideBundles && (
            <button className={tab==='bundles'?'on':''} onClick={()=>setTab('bundles')}>
              🎁 묶음 ({bundles.length})
            </button>
          )}
          <button className={tab==='fav'?'on':''} onClick={()=>setTab('fav')}>
            ★ 즐겨찾기 ({favProducts.length + favParts.length})
          </button>
          <button className={tab==='all'?'on':''} onClick={()=>setTab('all')}>
            전체
          </button>
        </div>

        {/* 묶음 탭 */}
        {tab === 'bundles' && (
          <>
            <input className="form-input" placeholder="묶음 검색"
              value={qBundle} onChange={e=>setQBundle(e.target.value)}
              style={{ marginBottom:10 }} />
            <div className="picker-modal-body">
              {filteredBundles.length === 0 ? (
                <div className="picker-empty">
                  {bundles.length === 0
                    ? '묶음이 없습니다.\n묶음 카탈로그에서 새로 만들어 보세요.'
                    : '검색 결과가 없습니다.'}
                </div>
              ) : (
                <div className="picker-snippet-list">
                  {filteredBundles.map(b => (
                    <div key={b.id} className="picker-snippet-card"
                      draggable={true}
                      onDragStart={(e)=>startDrag(e, { kind: 'bundle', bundleId: b.id })}
                      onDragEnd={endDrag}
                      onClick={()=>insertBundle(b)}
                      title="드래그 또는 클릭으로 묶음 추가 (원자 단위로 들어감)">
                      <div style={{ flex:1, minWidth:0 }}>
                        <div className="picker-snippet-name">🎁 {b.name}</div>
                        <div className="picker-snippet-meta">
                          {b.item_count}개 항목
                          {b.created_by_name && ` · 작성 ${b.created_by_name}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}


        {tab === 'fav' && (
          <>
            <input className="form-input" placeholder="즐겨찾기 안에서 검색"
              value={qFav} onChange={e=>setQFav(e.target.value)}
              style={{ marginBottom:10 }} />
            <div className="picker-modal-body">
              {filteredFavProducts.length === 0 && filteredFavParts.length === 0 ? (
                <div className="picker-empty">
                  즐겨찾기가 없습니다. "전체" 탭에서 ☆ 버튼으로 추가하세요.
                </div>
              ) : (
                <>
                  {filteredFavProducts.length > 0 && (
                    <div className="picker-section">
                      <div className="picker-section-title">상품 ({filteredFavProducts.length})</div>
                      <div className="picker-grid">
                        {filteredFavProducts.map(p => renderFavCard('product', p))}
                      </div>
                    </div>
                  )}
                  {filteredFavParts.length > 0 && (
                    <div className="picker-section">
                      <div className="picker-section-title">부품 ({filteredFavParts.length})</div>
                      <div className="picker-grid">
                        {filteredFavParts.map(p => renderFavCard('part', p))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {tab === 'all' && (
          <>
            <input className="form-input" placeholder="이름·모델·코드 검색"
              value={qAll} onChange={e=>setQAll(e.target.value)}
              autoFocus
              style={{ marginBottom:10 }} />
            <div className="picker-modal-body">
              {allProducts.length === 0 && allParts.length === 0 ? (
                <div className="picker-empty">검색 결과가 없습니다.</div>
              ) : (
                <>
                  {allProducts.length > 0 && (
                    <div className="picker-section">
                      <div className="picker-section-title">상품 ({allProducts.length}{products.length > allProducts.length ? ` / ${products.length}` : ''})</div>
                      <div className="picker-grid">
                        {allProducts.map(p => renderFavCard('product', p))}
                      </div>
                    </div>
                  )}
                  {allParts.length > 0 && (
                    <div className="picker-section">
                      <div className="picker-section-title">부품 ({allParts.length}{parts.length > allParts.length ? ` / ${parts.length}` : ''})</div>
                      <div className="picker-grid">
                        {allParts.map(p => renderFavCard('part', p))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* 푸터 — 드래그 안내 */}
        <div style={{ marginTop:12, paddingTop:10, borderTop:'1px solid #f1f5f9', fontSize:11, color:'#94a3b8', textAlign:'center' }}>
          💡 카드를 끌어다 견적 항목 영역에 놓거나 클릭으로 바로 추가하세요. 드래그하면 모달이 잠시 투명해집니다.
        </div>
      </div>
    </div>
  );
}
